import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreditCard, Search, RefreshCw, AlertTriangle, CheckCircle, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { handleEdgeFunctionError, formatErrorForToast } from "@/lib/errorHandler";

interface Purchase {
  id: string;
  user_id: string;
  purchase_type: string;
  credits_added: number;
  amount_paid: number | null;
  payment_status: string;
  created_at: string;
  refunded_at: string | null;
  refund_reason: string | null;
  can_refund: boolean;
  profiles: { full_name: string | null; user_id: string } | null;
}

export function PurchasesManager() {
  const { toast } = useToast();
  const { session } = useAuth();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState<"all" | "today" | "week" | "month" | "custom">("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: { action: "get_all_purchases", limit: 100 },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (error || data?.error) {
        const errorInfo = handleEdgeFunctionError(error, data, 'admin');
        toast(formatErrorForToast(errorInfo));
        return;
      }
      setPurchases(data?.purchases || []);
    } catch (error) {
      console.error("Error loading purchases:", error);
      const errorInfo = handleEdgeFunctionError(error, null, 'admin');
      toast(formatErrorForToast(errorInfo));
    } finally {
      setLoading(false);
    }
  };


  const getDateFilterRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateFilter) {
      case "today":
        return { start: today, end: new Date() };
      case "week":
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { start: weekAgo, end: new Date() };
      case "month":
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return { start: monthAgo, end: new Date() };
      case "custom":
        if (startDate && endDate) {
          return { 
            start: new Date(startDate), 
            end: new Date(new Date(endDate).setHours(23, 59, 59, 999)) 
          };
        }
        return null;
      default:
        return null;
    }
  };

  const filteredPurchases = purchases.filter((purchase) => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || (
      purchase.profiles?.full_name?.toLowerCase().includes(search) ||
      purchase.id.toLowerCase().includes(search) ||
      purchase.purchase_type.toLowerCase().includes(search)
    );

    const dateRange = getDateFilterRange();
    const matchesDate = !dateRange || (
      new Date(purchase.created_at) >= dateRange.start &&
      new Date(purchase.created_at) <= dateRange.end
    );

    return matchesSearch && matchesDate;
  });

  const totalRevenue = filteredPurchases
    .filter((p) => p.payment_status === "completed" && !p.refunded_at)
    .reduce((sum, p) => sum + (Number(p.amount_paid) || 0), 0);

  const totalRefunded = filteredPurchases
    .filter((p) => p.refunded_at)
    .reduce((sum, p) => sum + (Number(p.amount_paid) || 0), 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(amount);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Gestão de Compras e Reembolsos
              </CardTitle>
              <CardDescription>
                {filteredPurchases.length} compras {dateFilter !== "all" && "(filtradas)"} · R$ {totalRevenue.toFixed(2)} receita · R$ {totalRefunded.toFixed(2)} reembolsado
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadPurchases}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por usuário, ID ou tipo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={dateFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setDateFilter("all")}
              >
                Todas
              </Button>
              <Button
                variant={dateFilter === "today" ? "default" : "outline"}
                size="sm"
                onClick={() => setDateFilter("today")}
              >
                Hoje
              </Button>
              <Button
                variant={dateFilter === "week" ? "default" : "outline"}
                size="sm"
                onClick={() => setDateFilter("week")}
              >
                Última Semana
              </Button>
              <Button
                variant={dateFilter === "month" ? "default" : "outline"}
                size="sm"
                onClick={() => setDateFilter("month")}
              >
                Último Mês
              </Button>
              <Button
                variant={dateFilter === "custom" ? "default" : "outline"}
                size="sm"
                onClick={() => setDateFilter("custom")}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Personalizado
              </Button>
            </div>
          </div>

          {dateFilter === "custom" && (
            <div className="flex gap-2 items-center">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Data inicial"
                className="w-auto"
              />
              <span className="text-muted-foreground">até</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="Data final"
                className="w-auto"
              />
            </div>
          )}

          <div className="border rounded-lg">
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium">Data</th>
                    <th className="text-left p-3 text-sm font-medium">Usuário</th>
                    <th className="text-left p-3 text-sm font-medium">Tipo</th>
                    <th className="text-left p-3 text-sm font-medium">Créditos</th>
                    <th className="text-left p-3 text-sm font-medium">Valor</th>
                    <th className="text-left p-3 text-sm font-medium">Status</th>
                    <th className="text-left p-3 text-sm font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center p-8 text-muted-foreground">
                        Carregando compras...
                      </td>
                    </tr>
                  ) : filteredPurchases.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center p-8 text-muted-foreground">
                        Nenhuma compra encontrada
                      </td>
                    </tr>
                  ) : (
                    filteredPurchases.map((purchase) => (
                      <tr key={purchase.id} className="border-t hover:bg-muted/50">
                        <td className="p-3 text-sm">
                          {new Date(purchase.created_at).toLocaleDateString("pt-BR")}
                        </td>
                        <td className="p-3 text-sm">
                          {purchase.profiles?.full_name || "Unknown"}
                        </td>
                        <td className="p-3 text-sm">
                          <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                            {purchase.purchase_type}
                          </span>
                        </td>
                        <td className="p-3 text-sm font-medium">{purchase.credits_added}</td>
                        <td className="p-3 text-sm font-medium text-green-600 dark:text-green-400">
                          {formatCurrency(Number(purchase.amount_paid) || 0)}
                        </td>
                        <td className="p-3">
                          {purchase.refunded_at ? (
                            <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 flex items-center gap-1 w-fit">
                              <AlertTriangle className="w-3 h-3" />
                              Reembolsado
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 flex items-center gap-1 w-fit">
                              <CheckCircle className="w-3 h-3" />
                              {purchase.payment_status}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          {purchase.refunded_at && (
                            <span className="text-xs text-muted-foreground">
                              {purchase.refund_reason}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
